import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Avatar } from 'primereact/avatar';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { API_BASE_URL } from '../../../apiSetup/apiConfig';


interface TeacherDetails {
  id: number;
  user_id: number;
  name: string;
  gender: string;
  email_id: string;
  parentage: string;
  dob: string;
  address: string;
  contact_no: string;
  account_no: string;
  bank_name: string;
  ifsc_code: string;
  created_at: string;
  updated_at: string;
  profile_picture_url: string;
}

interface ViewTeacherModalProps {
  open: boolean;
  onClose: () => void;
  teacherId: string | null;
}

const ViewTeacherModal: React.FC<ViewTeacherModalProps> = ({ open, onClose, teacherId }) => {
  const toast = useRef<Toast>(null);
  const [teacherData, setTeacherData] = useState<TeacherDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const fetchTeacherData = async () => {
      if (!teacherId || !open) return;
      
      setLoading(true);
      setIsClosing(false);
      
      try {
        const response = await apiClient.get(`get-teacher?id=${teacherId}`);
        
        if (response.data.status === 'success') {
          const minLoadingTime = new Promise(resolve => setTimeout(resolve, 650));
          await minLoadingTime;
          setTeacherData(response.data.data);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: response.data.message || 'Failed to fetch teacher details',
            life: 5000
          });
          onClose();
        }
      } catch (error: any) {
        const message = error.response?.data?.message || error.message || 'Failed to fetch teacher details';
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: message,
          life: 5000
        });
        onClose();
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchTeacherData();
    }
  }, [open, teacherId, onClose]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) {
        dialog.classList.add('dialog-closing');
      }
      if (mask) {
        mask.classList.add('mask-closing');
      }
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setTeacherData(null);
      setLoading(true);
    }, 400);
  };

  const getGenderChipStyle = (gender: string) => {
    switch (gender.toLowerCase()) {
      case 'male':
        return { bg: '#3b82f6', text: 'Male' };
      case 'female':
        return { bg: '#ec4899', text: 'Female' };
      default:
        return { bg: '#f59e0b', text: gender };
    }
  };

  const InfoRow = ({ 
    icon, 
    label, 
    value, 
    isChip = false, 
    chipColor = '#4169E1' 
  }: { 
    icon: string;
    label: string;
    value: string | number;
    isChip?: boolean;
    chipColor?: string;
  }) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: '10px 0'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        borderRadius: '10px',
        background: 'rgba(65, 105, 225, 0.08)',
        color: '#4169E1',
        flexShrink: 0
      }}>
        <i className={icon} style={{ fontSize: '15px' }}></i>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '11px',
          fontWeight: 700,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
          marginBottom: '4px'
        }}>
          {label}
        </div>
        {isChip ? (
          <span style={{
            display: 'inline-block',
            padding: '5px 12px',
            borderRadius: '8px',
            background: chipColor,
            color: 'white',
            fontSize: '13px',
            fontWeight: 600,
            boxShadow: `0 2px 8px ${chipColor}40`
          }}>
            {value}
          </span>
        ) : (
          <div style={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#1f2937',
            lineHeight: 1.3,
            wordBreak: 'break-word'
          }}>
            {value}
          </div>
        )}
      </div>
    </div>
  );

  const SectionCard = ({ 
    title, 
    icon, 
    children 
  }: { 
    title: string;
    icon: string;
    children: React.ReactNode;
  }) => (
    <div style={{
      background: 'white',
      borderRadius: '16px',
      padding: '16px 18px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '14px',
        paddingBottom: '12px',
        borderBottom: '1px solid rgba(229, 231, 235, 0.6)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)',
          color: 'white'
        }}>
          <i className={icon} style={{ fontSize: '15px' }}></i>
        </div>
        <span style={{
          color: '#1e40af',
          fontWeight: 700,
          fontSize: '14px',
          letterSpacing: '0.02em',
          textTransform: 'uppercase'
        }}>
          {title}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {children}
      </div>
    </div>
  );

  const headerTemplate = () => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '8px 0'
    }}>
      <div style={{
        width: '84px',
        height: '84px',
        borderRadius: '14px',
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '44px',
        fontWeight: 700,
        boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
        flexShrink: 0,
        border: '3px solid white',
        overflow: 'hidden'
      }}>
        {teacherData?.profile_picture_url ? (
          <img 
            src={`${teacherData.profile_picture_url}`} 
            alt="Profile" 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <i className="pi pi-user"></i>
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: '20px',
          fontWeight: 700,
          color: '#1e40af',
          lineHeight: 1.2,
          marginBottom: '4px'
        }}>
          {teacherData?.name || 'Teacher Profile'}
        </div>
        <div style={{
          fontSize: '13px',
          color: '#64748b',
          fontWeight: 500
        }}>
          Complete Teacher Information
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      
      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ 
          width: '90vw',
          maxWidth: '800px',
          borderRadius: '24px',
          overflow: 'hidden'
        }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          border: '12px solid rgba(255, 255, 255, 0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none'
        }}
        headerStyle={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '16px 18px',
          borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255, 255, 255, 0.8)',
          borderRight: '12px solid rgba(255, 255, 255, 0.8)',
          borderTop: '12px solid rgba(255, 255, 255, 0.8)',
          margin: '-12px -12px 0 -12px'
        }}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        blockScroll
      >
        <style>{`
          .p-dialog-mask {
            backdrop-filter: blur(4px);
            background-color: rgba(0, 0, 0, 0.25) !important;
            animation: maskFadeIn 0.35s ease;
          }
          
          .p-dialog-mask.mask-closing {
            animation: maskFadeOut 0.4s ease !important;
          }
          
          @keyframes maskFadeIn {
            from {
              opacity: 0;
              background-color: rgba(0, 0, 0, 0);
            }
            to {
              opacity: 1;
              background-color: rgba(0, 0, 0, 0.25);
            }
          }
          
          @keyframes maskFadeOut {
            from {
              opacity: 1;
              background-color: rgba(0, 0, 0, 0.25);
            }
            to {
              opacity: 0;
              background-color: rgba(0, 0, 0, 0);
            }
          }
          
          .p-dialog {
            animation: dialogOpen 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          }
          
          .p-dialog.dialog-closing {
            animation: dialogClose 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
          }
          
          @keyframes dialogOpen {
            from {
              opacity: 0;
              transform: scale(0.92) translateY(-20px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
          
          @keyframes dialogClose {
            from {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
            to {
              opacity: 0;
              transform: scale(0.88) translateY(25px);
            }
          }
          
          .p-dialog .p-dialog-content::-webkit-scrollbar {
            width: 8px;
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track {
            background: rgba(241, 245, 249, 0.5);
            border-radius: 10px;
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb {
            background: rgba(148, 163, 184, 0.5);
            border-radius: 10px;
            transition: background 0.2s ease;
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover {
            background: rgba(100, 116, 139, 0.7);
          }
        `}</style>

        {loading ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '80px 20px',
            gap: '16px'
          }}>
            <ProgressSpinner 
              style={{ width: '50px', height: '50px' }}
              strokeWidth="4"
            />
            <span style={{
              color: '#64748b',
              fontSize: '14px',
              fontWeight: 600
            }}>
              Loading teacher details...
            </span>
          </div>
        ) : teacherData ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {/* Personal Information */}
            <SectionCard title="Personal Information" icon="pi pi-user">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '6px' }}>
                <InfoRow
                  icon="pi pi-user"
                  label="Full Name"
                  value={teacherData.name}
                />
                <InfoRow
                  icon="pi pi-users"
                  label="Gender"
                  value={teacherData.gender}
                />
                <InfoRow
                  icon="pi pi-calendar"
                  label="Date of Birth"
                  value={teacherData.dob}
                />
                <InfoRow
                  icon="pi pi-user"
                  label="Parentage"
                  value={teacherData.parentage}
                />
              </div>
            </SectionCard>

            {/* Contact Information */}
            <SectionCard title="Contact Information" icon="pi pi-phone">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '6px' }}>
                <InfoRow
                  icon="pi pi-envelope"
                  label="Email Address"
                  value={teacherData.email_id}
                />
                <InfoRow
                  icon="pi pi-phone"
                  label="Contact Number"
                  value={teacherData.contact_no}
                />
                <InfoRow
                  icon="pi pi-map-marker"
                  label="Address"
                  value={teacherData.address}
                />
              </div>
            </SectionCard>

            {/* Bank Information */}
            <SectionCard title="Bank Information" icon="pi pi-building">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '6px' }}>
                <InfoRow
                  icon="pi pi-credit-card"
                  label="Account Number"
                  value={teacherData.account_no}
                />
                <InfoRow
                  icon="pi pi-building"
                  label="Bank Name"
                  value={teacherData.bank_name}
                />
                <InfoRow
                  icon="pi pi-id-card"
                  label="IFSC Code"
                  value={teacherData.ifsc_code}
                />
              </div>
            </SectionCard>
          </div>
        ) : null}
      </Dialog>
    </>
  );
};

export default ViewTeacherModal;